import React, { createContext, useState, useCallback, useContext } from 'react';
import { ClickEffect } from '../view/shared/ClickEffect';
import { PotentialInfoModal } from '../view/shared/PotentialInfoModal';
import { FitnessInfoModal } from '../view/shared/FitnessInfoModal';
import { soundService } from '../services/SoundService';
import { useGameActions } from './GameActionsContext';
import { RatingInfoModal } from '../view/shared/RatingInfoModal';
import { MoraleInfoModal } from '../view/shared/MoraleInfoModal';
import { Morale } from '../domain/entities';

interface Click {
  id: number;
  x: number;
  y: number;
}

interface UIContextType {
  isMobileNavOpen: boolean;
  toggleMobileNav: () => void;
  showClickAt: (selector: string) => Promise<void>;
  openPotentialInfoModal: (potential: number) => void;
  openFitnessInfoModal: (fitness: number) => void;
  openRatingInfoModal: (leagueName: string, rating: number) => void;
  openMoraleInfoModal: (morale: Morale) => void;
  adsEnabled: boolean;
  toggleAds: () => void;
  isAiPunditEnabled: boolean;
  toggleAiPundit: () => void;
  showRatingsOnMatchReport: boolean;
  toggleShowRatingsOnMatchReport: () => void;
  showDebug: boolean;
  toggleDebug: () => void;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isPotentialInfoModalOpen, setIsPotentialInfoModalOpen] = useState(false);
  const [isFitnessInfoModalOpen, setIsFitnessInfoModalOpen] = useState(false);
  const [isRatingInfoModalOpen, setIsRatingInfoModalOpen] = useState(false);
  const [isMoraleInfoModalOpen, setIsMoraleInfoModalOpen] = useState(false);
  const [ratingModalLeagueName, setRatingModalLeagueName] = useState('');
  const [clicks, setClicks] = useState<Click[]>([]);

  // Separate state for each modal's current value
  const [currentPotential, setCurrentPotential] = useState<number | undefined>(undefined);
  const [currentFitness, setCurrentFitness] = useState<number | undefined>(undefined);
  const [currentRating, setCurrentRating] = useState<number | undefined>(undefined);
  const [currentMorale, setCurrentMorale] = useState<Morale | undefined>(undefined);

  const [adsEnabled, setAdsEnabled] = useState(true);
  const [isAiPunditEnabled, setIsAiPunditEnabled] = useState(false);
  const [showRatingsOnMatchReport, setShowRatingsOnMatchReport] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  const { setDebugFlag } = useGameActions();

  const toggleMobileNav = useCallback(() => {
    setIsMobileNavOpen(prev => !prev);
  }, []);

  const showClickAt = useCallback(async (selector: string) => {
    const element = document.querySelector(selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const id = Date.now() + Math.random();
      setClicks(prev => [...prev, { id, x, y }]);
      setTimeout(() => setClicks(prev => prev.filter(c => c.id !== id)), 800);

      // Actually trigger a click event on the element
      if (element instanceof HTMLElement) {
        element.click();
      }

      await new Promise(res => setTimeout(res, 600));
    }
  }, []);

  const openPotentialInfoModal = useCallback((potential: number) => {
    setCurrentPotential(potential);
    setIsPotentialInfoModalOpen(true);
  }, []);

  const openFitnessInfoModal = useCallback((fitness: number) => {
    setCurrentFitness(fitness);
    setIsFitnessInfoModalOpen(true);
  }, []);

  const openRatingInfoModal = useCallback((leagueName: string, rating: number) => {
    setRatingModalLeagueName(leagueName);
    setCurrentRating(rating);
    setIsRatingInfoModalOpen(true);
  }, []);

  const openMoraleInfoModal = useCallback((morale: Morale) => {
    setCurrentMorale(morale);
    setIsMoraleInfoModalOpen(true);
  }, []);

  const toggleAds = useCallback(() => {
    soundService.playClick();
    if (adsEnabled) { // This means it's currently ON and will be turned OFF
        setDebugFlag('adsDisabled');
    }
    setAdsEnabled(prev => !prev);
  }, [adsEnabled, setDebugFlag]);

  const toggleAiPundit = useCallback(() => {
    soundService.playClick();
    setIsAiPunditEnabled(prev => !prev);
  }, []);

  const toggleShowRatingsOnMatchReport = useCallback(() => {
    soundService.playClick();
    setShowRatingsOnMatchReport(prev => !prev);
  }, []);

  const toggleDebug = useCallback(() => {
    setShowDebug(prev => !prev);
  }, []);


  const value = {
    isMobileNavOpen,
    toggleMobileNav,
    showClickAt,
    openPotentialInfoModal,
    openFitnessInfoModal,
    openRatingInfoModal,
    openMoraleInfoModal,
    adsEnabled,
    toggleAds,
    isAiPunditEnabled,
    toggleAiPundit,
    showRatingsOnMatchReport,
    toggleShowRatingsOnMatchReport,
    showDebug,
    toggleDebug
  };

  return (
    <UIContext.Provider value={value}>
      {children}
      {clicks.map(click => (
        <ClickEffect key={click.id} x={click.x} y={click.y} />
      ))}
      <PotentialInfoModal isOpen={isPotentialInfoModalOpen} onClose={() => setIsPotentialInfoModalOpen(false)} currentPotential={currentPotential} />
      <FitnessInfoModal isOpen={isFitnessInfoModalOpen} onClose={() => setIsFitnessInfoModalOpen(false)} currentFitness={currentFitness} />
      <RatingInfoModal isOpen={isRatingInfoModalOpen} onClose={() => setIsRatingInfoModalOpen(false)} leagueName={ratingModalLeagueName} currentRating={currentRating} />
      <MoraleInfoModal isOpen={isMoraleInfoModalOpen} onClose={() => setIsMoraleInfoModalOpen(false)} currentMorale={currentMorale} />
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};
